import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { CreatorLibraryService } from '../../../services/creator-library.service';
import {
  CreatorStartDialogComponent,
  CreatorStartKind,
} from '../../../components/dialogs/creator-start-dialog/creator-start-dialog.component';
import { CreatorImportDialogComponent } from '../../../components/dialogs/creator-import-dialog/creator-import-dialog.component';

/** What the material zone takes (plans 4-01, 4-02): a Rise package or a document. */
export const IMPORT_FILE = /\.(zip|docx|pptx|pdf|md|txt)$/i;
/** Old Office formats the importer cannot read: the person saves them anew. */
const LEGACY_OFFICE = /\.(doc|ppt|pps|docm|pptm)$/i;

/** Query parameters that address the canvas itself — a link with one of them
 *  (old bookmarks, "Im Creator bearbeiten") goes on to the workspace. */
export const WORKSPACE_PARAMS = [
  'mode', 'title', 'description', 'exerciseType', 'exerciseTypes',
  'difficultyLevel', 'learningGoals', 'scope', 'bucketIds', 'item', 'kind',
];

/** The import formats shown in the material zone (icons by Marcel, 28.10.2026;
 *  product names, not translated). */
export const START_FORMATS: readonly { id: string; label: string }[] = [
  { id: 'figma', label: 'Figma' },
  { id: 'pdf', label: 'PDF' },
  { id: 'word', label: 'Word' },
  { id: 'powerpoint', label: 'PowerPoint' },
  { id: 'markdown', label: 'Markdown' },
  { id: 'articulate', label: 'Articulate' },
];

/**
 * Creator › start (`/creator`): what to make — a course or an exercise — and
 * the way into the library. A tile opens the start dialog with the ways to
 * it; `/creator/new-course` is this page with the course dialog open (route
 * data `open`). Native in the portal; the canvas frame opens only for AI and
 * the editors (`/creator/workspace`). Tenants without the library (rollout
 * allowlist, features/docs/technical/creator-items.md P1) keep the canvas
 * start page as before.
 */
@Component({
  selector: 'app-creator-start',
  templateUrl: './creator-start.component.html',
  styleUrl: './creator-start.component.scss',
})
export class CreatorStartComponent implements OnInit, OnDestroy {
  /** What the material zone will take, as file badges (product names, not translated). */
  readonly formats = START_FORMATS;
  /** Support access (impersonation) may only view unless al-filemanager allows
   *  writing (CREATOR_IMPERSONATION_WRITE): the library's answer decides
   *  (`readOnly`); until it is there, impersonation counts as view-only, so
   *  the ways to new content are shut instead of failing at the end. */
  supportAccess = this.auth.accessTokenPayload$.value?.impersonating === true;
  /** The material zone takes an Articulate Rise package (.zip) and Word,
   *  PowerPoint, PDF, Markdown and text where the course editor runs; Figma
   *  stays „Bald" (plans 4-01, 4-02, creator-items.md „Import"). */
  get importAvailable(): boolean {
    return !!environment.creatorCourseEditor && !this.supportAccess;
  }
  dragging = false;
  state: 'checking' | 'ready' | 'error' = 'checking';
  private sub?: Subscription;
  private dialogOpen = false;

  constructor(
    private library: CreatorLibraryService,
    private route: ActivatedRoute,
    private router: Router,
    private dialogs: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    if (WORKSPACE_PARAMS.some((key) => params[key])) {
      this.router.navigate(['/creator/workspace'], { queryParams: params, replaceUrl: true });
      return;
    }
    this.check();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  check(): void {
    this.state = 'checking';
    this.sub?.unsubscribe();
    this.sub = this.library.access().subscribe(({ answer, readOnly }) => {
      if (answer === 'yes') this.supportAccess = readOnly;
      if (answer === 'no') {
        this.router.navigate(['/creator/workspace'], { replaceUrl: true });
        return;
      }
      this.state = answer === 'yes' ? 'ready' : 'error';
      const preset = this.route.snapshot.data['open'] as CreatorStartKind | undefined;
      if (this.state === 'ready' && preset) this.open(preset);
    });
  }

  onFileChosen(input: HTMLInputElement): void {
    const file = input.files?.[0];
    input.value = ''; // the same file again opens the dialog again
    if (file) this.importFile(file);
  }

  onDragOver(event: DragEvent): void {
    if (!this.importAvailable) return;
    event.preventDefault();
    this.dragging = true;
  }

  onDrop(event: DragEvent): void {
    if (!this.importAvailable) return;
    event.preventDefault();
    this.dragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.importFile(file);
  }

  importFile(file: File): void {
    if (!IMPORT_FILE.test(file.name)) {
      const message = LEGACY_OFFICE.test(file.name) ? 'pages.creator.import.errors.legacyOffice' : 'pages.creator.start.zipOnly';
      this.toastr.info(this.t.instant(message), '');
      return;
    }
    this.dialogs.open(CreatorImportDialogComponent, { context: { file }, closeOnBackdropClick: false, closeOnEsc: false });
  }

  /** Without the course editor the material zone takes no files; its button says so. */
  importSoon(): void {
    this.toastr.info(this.t.instant('pages.creator.start.importSoon'), this.t.instant('pages.creator.start.soon'));
  }

  open(kind: CreatorStartKind): void {
    if (this.dialogOpen || this.supportAccess) return;
    this.dialogOpen = true;
    this.dialogs
      .open(CreatorStartDialogComponent, { context: { kind }, autoFocus: true })
      .onClose.subscribe(() => {
        this.dialogOpen = false;
        // the deep link opened the dialog; closed, the page is the start again
        if (this.route.snapshot.data['open'] && this.router.url.startsWith('/creator/new-course')) {
          this.router.navigate(['/creator'], { replaceUrl: true });
        }
      });
  }
}
